/**
 * Small, dependency-free charts for the dashboard. One series each (brand purple, validated
 * against the surface), thin marks with 4px rounded data ends, values in text ink, a hover and
 * focus tooltip on every mark, and a table view so nothing depends on colour or hover alone.
 */
import { useId, useState, type ReactNode } from "react";

interface Point { key: string; label: string; value: number; display: string; detail?: string }

function Tooltip({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div role="tooltip" className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md border border-line bg-surface px-2.5 py-1.5 text-xs shadow-lg">
      <p className="font-semibold text-ink">{title}</p>
      <div className="text-muted">{children}</div>
    </div>
  );
}

function TableView({ caption, points, valueHeader }: { caption: string; points: Point[]; valueHeader: string }) {
  return (
    <table className="w-full text-left text-sm">
      <caption className="sr-only">{caption}</caption>
      <thead className="text-xs uppercase tracking-wide text-muted"><tr><th className="py-1">Item</th><th className="py-1 text-right">{valueHeader}</th><th className="py-1 text-right">Detail</th></tr></thead>
      <tbody>{points.map((p) => <tr key={p.key} className="border-t border-line"><td className="py-1.5">{p.label}</td><td className="py-1.5 text-right tabular-nums">{p.display}</td><td className="py-1.5 text-right text-muted">{p.detail}</td></tr>)}</tbody>
    </table>
  );
}

function ChartFrame({ title, subtitle, table, children }: { title: string; subtitle?: string; table: ReactNode; children: ReactNode }) {
  const [asTable, setAsTable] = useState(false);
  return (
    <section className="card p-5" aria-label={title}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div><h2 className="font-semibold">{title}</h2>{subtitle && <p className="text-xs text-muted">{subtitle}</p>}</div>
        <button type="button" className="text-xs font-medium text-brand hover:underline" aria-pressed={asTable} onClick={() => setAsTable((v) => !v)}>
          {asTable ? "Show chart" : "Show table"}
        </button>
      </div>
      {asTable ? table : children}
    </section>
  );
}

/** Horizontal bars: one measure across named items (pipeline value by stage). */
export function BarList({ title, subtitle, points, valueHeader }: { title: string; subtitle?: string; points: Point[]; valueHeader: string }) {
  const max = Math.max(...points.map((p) => p.value), 1);
  const [hover, setHover] = useState<string | null>(null);
  return (
    <ChartFrame title={title} subtitle={subtitle} table={<TableView caption={title} points={points} valueHeader={valueHeader} />}>
      <ul className="space-y-2.5">
        {points.map((p) => (
          <li key={p.key} className="grid grid-cols-[minmax(6rem,9rem)_1fr_auto] items-center gap-3 text-sm">
            <span className="truncate text-muted" title={p.label}>{p.label}</span>
            <span className="relative h-5" onMouseEnter={() => setHover(p.key)} onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(p.key)} onBlur={() => setHover(null)} tabIndex={0} aria-label={`${p.label}: ${p.display}${p.detail ? `, ${p.detail}` : ""}`}>
              <span className="absolute inset-y-0.5 left-0 rounded-r-[4px] bg-brand transition-[width]" style={{ width: `${Math.max((p.value / max) * 100, p.value > 0 ? 1.5 : 0)}%` }} />
              {hover === p.key && <span className="absolute left-1/2 top-0"><Tooltip title={p.label}>{p.display}{p.detail && <> · {p.detail}</>}</Tooltip></span>}
            </span>
            <span className="text-right tabular-nums text-ink">{p.display}</span>
          </li>
        ))}
      </ul>
    </ChartFrame>
  );
}

/** Columns over time: one measure per month (won value), baseline at zero. */
export function ColumnChart({ title, subtitle, points, valueHeader, ticks }: {
  title: string; subtitle?: string; points: Point[]; valueHeader: string; ticks: (v: number) => string;
}) {
  const id = useId();
  const max = Math.max(...points.map((p) => p.value), 1);
  const nice = niceMax(max);
  const [hover, setHover] = useState<string | null>(null);
  return (
    <ChartFrame title={title} subtitle={subtitle} table={<TableView caption={title} points={points} valueHeader={valueHeader} />}>
      <div className="grid grid-cols-[auto_1fr] gap-2" aria-describedby={id}>
        <div className="flex h-44 flex-col justify-between text-right text-[11px] tabular-nums text-muted">
          {[nice, nice / 2, 0].map((t) => <span key={t}>{ticks(t)}</span>)}
        </div>
        <div className="relative h-44 border-b border-line">
          <div className="absolute inset-x-0 top-0 border-t border-dashed border-line" />
          <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-line" />
          <div className="relative flex h-full items-end gap-3 px-1">
            {points.map((p) => (
              <div key={p.key} className="relative flex h-full flex-1 items-end justify-center"
                onMouseEnter={() => setHover(p.key)} onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(p.key)} onBlur={() => setHover(null)} tabIndex={0}
                aria-label={`${p.label}: ${p.display}${p.detail ? `, ${p.detail}` : ""}`}>
                <span className="w-full max-w-10 rounded-t-[4px] bg-brand" style={{ height: `${(p.value / nice) * 100}%`, minHeight: p.value > 0 ? 2 : 0 }} />
                {hover === p.key && <span className="absolute left-1/2" style={{ bottom: `${(p.value / nice) * 100}%` }}><Tooltip title={p.label}>{p.display}{p.detail && <> · {p.detail}</>}</Tooltip></span>}
              </div>
            ))}
          </div>
        </div>
        <span />
        <div className="flex gap-3 px-1 text-center text-[11px] text-muted">{points.map((p) => <span key={p.key} className="flex-1">{p.label}</span>)}</div>
      </div>
      <p id={id} className="sr-only">{points.map((p) => `${p.label} ${p.display}`).join(", ")}</p>
    </ChartFrame>
  );
}

function niceMax(v: number): number {
  const exp = 10 ** Math.floor(Math.log10(v));
  const f = v / exp;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * exp;
}

export function StatTile({ label, value, note, tone }: { label: string; value: string; note?: string; tone?: "danger" | "ok" | "warn" }) {
  return (
    <div className="card p-4">
      <p className="text-xs font-medium text-muted">{label}</p>
      <p className={`mt-1 text-2xl font-semibold tracking-tight ${tone === "danger" ? "text-danger" : tone === "warn" ? "text-warn" : ""}`}>{value}</p>
      {note && <p className="mt-0.5 text-xs text-muted">{note}</p>}
    </div>
  );
}
